import { useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Keyboard behaviour shared by the modal dialogs: focus moves into the dialog
 * on mount, Tab stays inside while it is open, Escape calls `onEscape`, and
 * focus goes back to whatever opened it when it unmounts.
 *
 * `containerRef` must point at an element with tabIndex={-1}: it takes focus
 * when there is no `initialRef` yet (a dialog still waiting for its data), so
 * a keyboard user is never left behind the backdrop. The effect runs once per
 * mount on purpose - the latest `onEscape` is read through a ref, otherwise
 * every re-render would "restore" focus to the trigger.
 */
export default function useDialogFocus(containerRef, { initialRef, onEscape } = {}) {
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  useEffect(() => {
    const previous = document.activeElement;
    (initialRef?.current || containerRef.current)?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        escapeRef.current?.();
        return;
      }
      const box = containerRef.current;
      if (e.key !== 'Tab' || !box) return;
      const items = box.querySelectorAll(FOCUSABLE);
      if (!items.length) { e.preventDefault(); box.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!box.contains(active) || (active === box && !e.shiftKey)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === box)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (previous && previous.focus && document.contains(previous)) previous.focus();
    };
    // Deliberately empty deps: once per mount, see above.
  }, []);
}
